import React, { useState, useCallback, useEffect } from 'react';
import { Grid, Modal } from '~/components';
import { MdFilterList, MdClose, MdStar, MdStarBorder } from 'react-icons/md';
import { FormGroup } from '~/components/form';
import { useForm } from '~/hooks';
import { applyFilters } from './filters';
import omitBy from 'lodash/omitBy';
import sectionTypes from '../sectionTypes';
import { sortStrings } from '~/util/sorters';
import { orderBy } from 'lodash';

const defaultState = {
    id: 0,
    sectionId: 0,
    relationshipOptions: [],
    sectionDetails: [],
    data: [],
    addLenderModalFLag: false,
    lenderDetailData: {},
    editLender: false,
    sectionType: sectionTypes.TITLE_INSURANCE_APPLICATION,
};

const headerClassName = 'bg-gray-50 p-4 border-b text-left';

const actionColumnStyles = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridGap: '8px',
    justifyContent: 'center',
    alignItems: 'center'
};

const LenderLookupGrid = (props) => {
    const {
        onLenderSelect,
        data,
        favourites,
        addFavourite,
        removeFavourite,
        ...rest
    } = props;

    const [filtersOpen, setFiltersOpen] = useState(false);
    const loadData = async (values) => {
        return {
            ...values
        };
    };

    const onLoad = useCallback((values) => {
        return loadData(values);
    }, [props.sectionDetails]);

    const {
        state: { values: filters },
        fieldActions,
        formActions,
    } = useForm({
        initialState: { ...defaultState },
        onLoad
    });


    let [rows, setRows] = useState([]);

    useEffect(() => {
        const orderedRows = orderBy(data.map(row => ({
            ...row,
            position: favourites?.includes(row.id) ? -1 : 0
        })), 'position');

        setRows(orderedRows);
    }, [data, favourites]);

    const clean = (filters) => {
        return omitBy(filters, (value, key) => value == null || /options/i.test(key));
    };

    const filterData = () => {
        const filtered = applyFilters(data, clean(filters));
        setRows(filtered);
        setFiltersOpen(false);
    };

    const clearFilters = () => {
        formActions.reset();
        setRows([...data]);
        setFiltersOpen(false);
    };

    const columns = [
        {
            title: '',
            field: 'id',
            canSort: false,
            cell: ({ data }) => {
                return (
                    <div style={actionColumnStyles}>
                        {data.position === -1 ? (
                            <MdStar size={21} color="orange" style={{ cursor: 'pointer' }} onClick={() => removeFavourite(data.id)} />
                        ) : (
                            <MdStarBorder size={21} color="rgb(113, 113, 122)" style={{ cursor: 'pointer' }} onClick={() => addFavourite(data.id)} />
                        )}
                        <input type="radio" name="lender"
                            onChange={() => onLenderSelect(data)}
                        />
                    </div>
                );
            }
        },
        {
            title: 'Lender',
            field: 'lenderName',
            headerClassName,
            props: rest
        },
        {
            title: 'Address',
            cell: ({ data }) => {
                return (
                    <>
                        <div>{data.lenderStreetAddress + ' ' + data.lenderCity + ' ' + data.lenderState + ' ' + data.lenderZip}</div>
                    </>
                );
            },
            sorter: (a, b) => {
                return sortStrings(
                    `${a.lenderStreetAddress} ${a.lenderCity} ${a.lenderState} ${a.lenderZip}`,
                    `${b.lenderStreetAddress} ${b.lenderCity} ${b.lenderState} ${b.lenderZip}`
                );
            },
            headerClassName,
            props: rest
        }
    ];

    return (
        <div className='w-full col-span-4 flex flex-col items-stretch justify-center'>
            <div className='flex items-center'>
                <button
                    className='py-2 px-4 flex text-blue-dark text-sm font-semibold bg-gray-50 focus:outline-none items-center justify-center rounded-full'
                    onClick={() => setFiltersOpen(true)}
                >
                    <MdFilterList size='1.125em' className='mr-1' />
                    Filter
                </button>
                <Modal
                    isModalOpen={filtersOpen}
                    className='bg-white p-4 rounded-md'>
                    <section className='flex flex-col items-stretch justify-start'>
                        <header className="border-b-2 text-sm flex items-center justify-between py-2">
                            <p className="font-semibold">Filters</p>
                            <button
                                className="py-2 rounded-full"
                                onClick={() => setFiltersOpen(false)}
                            >
                                <MdClose size="1em" />
                            </button>
                        </header>
                        <div className='flex items-center justify-center'>
                            <div className='grid grid-cols-12 gap-y-4'>
                                <FormGroup
                                    inputClassName='mt-2 border-1 border-gray-200 rounded bg-gray-50'
                                    labelClassName='text-sm'
                                    groupClassName='col-span-5'
                                    label='Lender'
                                    value={filters.lender}
                                    path='lender'
                                    {...fieldActions}
                                />
                                <span className='col-span-1' />
                                <FormGroup
                                    inputClassName='mt-2 border-1 border-gray-200 rounded bg-gray-50'
                                    labelClassName='text-sm'
                                    groupClassName='col-span-6'
                                    label='Address'
                                    value={filters.address}
                                    path='address'
                                    {...fieldActions}
                                />
                                <FormGroup
                                    inputClassName='w-full mt-2 border-1 border-gray-200 rounded bg-gray-50'
                                    labelClassName='text-sm'
                                    groupClassName='col-span-5'
                                    label='State'
                                    value={filters.state}
                                    path='state'
                                    {...fieldActions}
                                />
                                <span className='col-span-1' />
                                <FormGroup
                                    inputClassName='mt-2 border-1 border-gray-200 rounded bg-gray-50'
                                    labelClassName='text-sm'
                                    groupClassName='col-span-6'
                                    label='City'
                                    value={filters.city}
                                    path='city'
                                    {...fieldActions}
                                />
                                <FormGroup
                                    inputClassName='mt-2 border-1 border-gray-200 rounded bg-gray-50'
                                    labelClassName='text-sm'
                                    groupClassName='col-span-5'
                                    label='Zip Code'
                                    value={filters.zipcode}
                                    path='zipcode'
                                    {...fieldActions}
                                />
                                <span className='col-span-6' />
                                <div className='col-span-11 flex items-center justify-end text-xs mt-4'>
                                    <button
                                        className='py-2 px-4 rounded-full border mr-2 focus:outline-none'
                                        onClick={clearFilters}
                                    >
                                        Clear Filters
                                    </button>
                                    <button
                                        className='py-2 px-4 rounded-full bg-blue-50 text-white focus:outline-none'
                                        onClick={filterData}
                                    >
                                        Apply
                                    </button>
                                </div>
                            </div>
                        </div>
                    </section>
                </Modal>
            </div>
            <Grid
                usePagination
                initialState={{
                    defaultPageSize: 5
                }}
                data={rows}
                columns={columns}
                className='w-full my-4 rounded-md'
                tableClassName='mb-1'
            />
        </div>
    );
};

export default LenderLookupGrid;